import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthState } from "src/types/authTenant.types";

const initialState: AuthState = {
  login: {
    loading: false,
    data: null,
    error: null,
    errorCode: null,
  },
  isAuthenticated: false,
  user: {
    loading: false,
    data: null,
    error: null,
  },
  editUser: {
    loading: false,
    data: null,
    error: null,
  },
  editCompanyDetails: {
    loading: false,
    data: null,
    error: null,
  },
  // Self-serve sign-up wizard: email -> OTP -> org details -> owner details.
  sendSignupOtp: {
    loading: false,
    data: null,
    error: null,
  },
  verifySignupOtp: {
    loading: false,
    data: null,
    error: null,
  },
  completeSignup: {
    loading: false,
    data: null,
    error: null,
  },
  sendEmailOtp: {
    loading: false,
    data: null,
    error: null,
  },
  verifyEmailOtp: {
    loading: false,
    data: null,
    error: null,
  },
  forgotPassword: {
    loading: false,
    data: null,
    error: null,
  },
  resetPassword: {
    loading: false,
    data: null,
    error: null,
  },
};

const authSlice = createSlice({
  name: "authTenant",
  initialState,
  reducers: {
    loginRequest: (
      state,
      action: PayloadAction<{ email: string; password: string }>
    ) => {
      state.login.loading = true;
      state.login.data = null;
      state.login.error = null;
      state.login.errorCode = null;
    },
    loginRequestSuccess: (state, action: PayloadAction<AuthState["user"]>) => {
      state.isAuthenticated = true;
      state.login.loading = false;
      state.login.data = action.payload;
      state.login.error = null;
      state.login.errorCode = null;
    },
    loginRequestError: (
      state,
      action: PayloadAction<{ message: string; code?: string }>
    ) => {
      state.isAuthenticated = false;
      state.login.loading = false;
      state.login.data = null;
      state.login.error = action.payload.message;
      state.login.errorCode = action.payload.code || null;
    },

    getUserDetailsRequest: (state, action: PayloadAction) => {
      state.user.loading = true;
      state.user.data = null;
      state.user.error = null;
    },
    getUserDetailsSuccess: (
      state,
      action: PayloadAction<AuthState["user"]>
    ) => {
      state.isAuthenticated = true;
      state.user.loading = false;
      state.user.data = action.payload.data;
      state.user.error = null;
    },
    getUserDetailsError: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user.loading = false;
      state.user.data = null;
      state.user.error = action.payload;
    },

    // edit tenant user actions
    editUserRequest: (
      state,
      action: PayloadAction<{
        firstName: string;
        lastName?: string;
        email: string;
        password?: string;
      }>
    ) => {
      state.editUser.loading = true;
      state.editUser.data = null;
      state.editUser.error = null;
    },
    editUserSuccess: (state, action: PayloadAction<any>) => {
      state.editUser.loading = false;
      state.editUser.data = action.payload;
      state.editUser.error = null;
    },
    editUserError: (state, action: PayloadAction<string>) => {
      state.editUser.loading = false;
      state.editUser.data = null;
      state.editUser.error = action.payload;
    },
    resetEditUser: (state) => {
      state.editUser.loading = false;
      state.editUser.data = null;
      state.editUser.error = null;
    },

    // edit company (tenant) details actions
    editCompanyDetailsRequest: (
      state,
      action: PayloadAction<{
        gst?: string;
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        pincode?: string;
        idPrefixes?: {
          saleOrder?: string;
          purchaseOrder?: string;
          product?: string;
          adjustmentOrder?: string;
          transaction?: string;
          transfer?: string;
        };
      }>
    ) => {
      state.editCompanyDetails.loading = true;
      state.editCompanyDetails.data = null;
      state.editCompanyDetails.error = null;
    },
    editCompanyDetailsSuccess: (state, action: PayloadAction<any>) => {
      state.editCompanyDetails.loading = false;
      state.editCompanyDetails.data = action.payload;
      state.editCompanyDetails.error = null;
    },
    editCompanyDetailsError: (state, action: PayloadAction<string>) => {
      state.editCompanyDetails.loading = false;
      state.editCompanyDetails.data = null;
      state.editCompanyDetails.error = action.payload;
    },
    resetEditCompanyDetails: (state) => {
      state.editCompanyDetails.loading = false;
      state.editCompanyDetails.data = null;
      state.editCompanyDetails.error = null;
    },

    // setUser: (state, action: PayloadAction<AuthState["user"]>) => {
    //   state.user = action.payload;
    // },

    // Step 1 + resend: just an email, sends an OTP.
    sendSignupOtpRequest: (
      state,
      action: PayloadAction<{ email: string }>
    ) => {
      state.sendSignupOtp.loading = true;
      state.sendSignupOtp.data = null;
      state.sendSignupOtp.error = null;
    },
    sendSignupOtpSuccess: (state, action: PayloadAction<any>) => {
      state.sendSignupOtp.loading = false;
      state.sendSignupOtp.data = action.payload;
      state.sendSignupOtp.error = null;
    },
    sendSignupOtpError: (state, action: PayloadAction<string>) => {
      state.sendSignupOtp.loading = false;
      state.sendSignupOtp.data = null;
      state.sendSignupOtp.error = action.payload;
    },
    resetSendSignupOtp: (state) => {
      state.sendSignupOtp.loading = false;
      state.sendSignupOtp.data = null;
      state.sendSignupOtp.error = null;
    },

    // Step 2: confirm the OTP.
    verifySignupOtpRequest: (
      state,
      action: PayloadAction<{ email: string; code: string }>
    ) => {
      state.verifySignupOtp.loading = true;
      state.verifySignupOtp.data = null;
      state.verifySignupOtp.error = null;
    },
    verifySignupOtpSuccess: (state, action: PayloadAction<any>) => {
      state.verifySignupOtp.loading = false;
      state.verifySignupOtp.data = action.payload;
      state.verifySignupOtp.error = null;
    },
    verifySignupOtpError: (state, action: PayloadAction<string>) => {
      state.verifySignupOtp.loading = false;
      state.verifySignupOtp.data = null;
      state.verifySignupOtp.error = action.payload;
    },
    resetVerifySignupOtp: (state) => {
      state.verifySignupOtp.loading = false;
      state.verifySignupOtp.data = null;
      state.verifySignupOtp.error = null;
    },

    // Steps 3+4: org + owner details, submitted together at the end.
    completeSignupRequest: (
      state,
      action: PayloadAction<{
        email: string;
        organizationName: string;
        organizationIdentifier: string;
        ownerName: string;
        mobile: string;
        password: string;
        warehouseLabelSingular?: string;
        warehouseLabelPlural?: string;
      }>
    ) => {
      state.completeSignup.loading = true;
      state.completeSignup.data = null;
      state.completeSignup.error = null;
    },
    completeSignupSuccess: (state, action: PayloadAction<any>) => {
      state.completeSignup.loading = false;
      state.completeSignup.data = action.payload;
      state.completeSignup.error = null;
    },
    completeSignupError: (state, action: PayloadAction<string>) => {
      state.completeSignup.loading = false;
      state.completeSignup.data = null;
      state.completeSignup.error = action.payload;
    },

    // profile email verification actions (covers admin-created tenants)
    sendEmailOtpRequest: (state) => {
      state.sendEmailOtp.loading = true;
      state.sendEmailOtp.data = null;
      state.sendEmailOtp.error = null;
    },
    sendEmailOtpSuccess: (state, action: PayloadAction<any>) => {
      state.sendEmailOtp.loading = false;
      state.sendEmailOtp.data = action.payload;
      state.sendEmailOtp.error = null;
    },
    sendEmailOtpError: (state, action: PayloadAction<string>) => {
      state.sendEmailOtp.loading = false;
      state.sendEmailOtp.data = null;
      state.sendEmailOtp.error = action.payload;
    },

    verifyEmailOtpRequest: (
      state,
      action: PayloadAction<{ code: string }>
    ) => {
      state.verifyEmailOtp.loading = true;
      state.verifyEmailOtp.data = null;
      state.verifyEmailOtp.error = null;
    },
    verifyEmailOtpSuccess: (state, action: PayloadAction<any>) => {
      state.verifyEmailOtp.loading = false;
      state.verifyEmailOtp.data = action.payload;
      state.verifyEmailOtp.error = null;
      if (state.user.data) {
        state.user.data.emailVerified = true;
      }
    },
    verifyEmailOtpError: (state, action: PayloadAction<string>) => {
      state.verifyEmailOtp.loading = false;
      state.verifyEmailOtp.data = null;
      state.verifyEmailOtp.error = action.payload;
    },
    resetVerifyEmailOtp: (state) => {
      state.verifyEmailOtp.loading = false;
      state.verifyEmailOtp.data = null;
      state.verifyEmailOtp.error = null;
    },

    // forgot password actions
    forgotPasswordRequest: (
      state,
      action: PayloadAction<{ email: string }>
    ) => {
      state.forgotPassword.loading = true;
      state.forgotPassword.data = null;
      state.forgotPassword.error = null;
    },
    forgotPasswordSuccess: (state, action: PayloadAction<any>) => {
      state.forgotPassword.loading = false;
      state.forgotPassword.data = action.payload;
      state.forgotPassword.error = null;
    },
    forgotPasswordError: (state, action: PayloadAction<string>) => {
      state.forgotPassword.loading = false;
      state.forgotPassword.data = null;
      state.forgotPassword.error = action.payload;
    },

    // reset password actions
    resetPasswordRequest: (
      state,
      action: PayloadAction<{ email: string; token: string; newPassword: string }>
    ) => {
      state.resetPassword.loading = true;
      state.resetPassword.data = null;
      state.resetPassword.error = null;
    },
    resetPasswordSuccess: (state, action: PayloadAction<any>) => {
      state.resetPassword.loading = false;
      state.resetPassword.data = action.payload;
      state.resetPassword.error = null;
    },
    resetPasswordError: (state, action: PayloadAction<string>) => {
      state.resetPassword.loading = false;
      state.resetPassword.data = null;
      state.resetPassword.error = action.payload;
    },

    logout: (state) => {
      state.isAuthenticated = false;
      state.user.data = null;
      state.login.loading = false;
      state.login.data = null;
      state.login.error = null;
      state.login.errorCode = null;
    },
  },
});

export const {
  // Login actions
  loginRequest,
  loginRequestSuccess,
  loginRequestError,

  // Get User Details Actions
  getUserDetailsRequest,
  getUserDetailsSuccess,
  getUserDetailsError,

  // Edit User Actions
  editUserRequest,
  editUserSuccess,
  editUserError,
  resetEditUser,

  // Edit Company Details Actions
  editCompanyDetailsRequest,
  editCompanyDetailsSuccess,
  editCompanyDetailsError,
  resetEditCompanyDetails,

  // Self-serve sign-up wizard actions
  sendSignupOtpRequest,
  sendSignupOtpSuccess,
  sendSignupOtpError,
  resetSendSignupOtp,
  verifySignupOtpRequest,
  verifySignupOtpSuccess,
  verifySignupOtpError,
  resetVerifySignupOtp,
  completeSignupRequest,
  completeSignupSuccess,
  completeSignupError,

  // Profile email verification actions
  sendEmailOtpRequest,
  sendEmailOtpSuccess,
  sendEmailOtpError,
  verifyEmailOtpRequest,
  verifyEmailOtpSuccess,
  verifyEmailOtpError,
  resetVerifyEmailOtp,

  // Forgot password actions
  forgotPasswordRequest,
  forgotPasswordSuccess,
  forgotPasswordError,

  // Reset password actions
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordError,

  // user actions
  // setUser,

  // Logout action
  logout,
} = authSlice.actions;
export default authSlice.reducer;
